import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AdminService from "../../../api/services/adminService";
import "../Home/Homepage.css";
import "../System/BonusManagementPage.css";

const defaultForm = {
  usdt_price: "",
  zil_price: "",
};

const toNumberOrEmpty = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

const ZilPricingSettingsPage = () => {
  const [form, setForm] = useState(defaultForm);
  const [isConfigured, setIsConfigured] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });

  const updateField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    if (message.text) setMessage({ type: "", text: "" });
  };

  const fetchPricing = async () => {
    setLoading(true);
    setMessage({ type: "", text: "" });
    try {
      const res = await AdminService.getZilPricing();
      if (res?.success && res?.data) {
        setForm({
          usdt_price:
            res.data.usdt_price != null ? String(res.data.usdt_price) : "",
          zil_price: res.data.zil_price != null ? String(res.data.zil_price) : "",
        });
        setIsConfigured(!!res.data.is_configured);
      } else {
        setMessage({ type: "error", text: res?.message || "Failed to fetch ZILN pricing." });
      }
    } catch (err) {
      setMessage({
        type: "error",
        text: err?.response?.data?.message || err?.message || "Failed to fetch ZILN pricing.",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPricing();
  }, []);

  const handleSave = async () => {
    const usdt_price = toNumberOrEmpty(form.usdt_price);
    const zil_price = toNumberOrEmpty(form.zil_price);

    if (usdt_price == null || usdt_price <= 0) {
      setMessage({ type: "error", text: "USDT price must be greater than 0." });
      return;
    }
    if (zil_price == null || zil_price <= 0) {
      setMessage({ type: "error", text: "ZILN price must be greater than 0." });
      return;
    }

    const payload = { usdt_price, zil_price };
    setSaving(true);
    setMessage({ type: "", text: "" });
    try {
      const res = await AdminService.updateZilPricing(payload);
      if (res?.success) {
        setMessage({ type: "success", text: res?.message || "ZILN pricing saved successfully." });
        if (res?.data) {
          setForm({
            usdt_price:
              res.data.usdt_price != null ? String(res.data.usdt_price) : String(usdt_price),
            zil_price:
              res.data.zil_price != null ? String(res.data.zil_price) : String(zil_price),
          });
          setIsConfigured(!!res.data.is_configured);
        } else {
          setIsConfigured(true);
        }
      } else {
        setMessage({ type: "error", text: res?.message || "Failed to save ZILN pricing." });
      }
    } catch (err) {
      setMessage({
        type: "error",
        text: err?.response?.data?.message || err?.message || "Failed to save ZILN pricing.",
      });
    } finally {
      setSaving(false);
    }
  };

  const rateHint =
    form.usdt_price && form.zil_price && Number(form.zil_price) > 0
      ? `1 USDT ≈ ${(Number(form.usdt_price) / Number(form.zil_price)).toLocaleString("en-IN", { maximumFractionDigits: 8 })} ZILN`
      : null;

  return (
    <main className="dashboard-main dashboard-subpage bonus-management-page">
      <section className="dashboard-hero bonus-hero">
        <div className="dashboard-hero__text">
          <p className="dashboard-hero__breadcrumb">
            <Link to="/dashboard/homepage">Dashboard</Link>
            <span style={{ margin: "0 0.35rem" }}>/</span>
            <span>Settings</span>
            <span style={{ margin: "0 0.35rem" }}>/</span>
            <span>ZILN Pricing</span>
          </p>
          <h1 className="dashboard-hero__title">ZILN Pricing</h1>
          <p className="dashboard-hero__subtitle">
            Set USDT and ZILN reference prices used for admin staking conversions
          </p>
        </div>
        <div className="bonus-hero__icon">
          <i className="fas fa-tags" />
        </div>
      </section>

      <div className="dashboard-body">
        <section className="bonus-card">
          <h2 className="bonus-card__title">
            <i className="fas fa-coins" /> Price configuration
          </h2>
          <p style={{ margin: "0 0 1rem", fontSize: "0.88rem", color: "#64748b", lineHeight: 1.5 }}>
            These rates power the USDT → ZILN converter on{" "}
            <Link to="/dashboard/staking/subscribe-by-admin">Subscribe by admin</Link> when a ZILN
            package is selected.
          </p>

          {loading ? (
            <div className="bonus-loading">
              <i className="fas fa-spinner fa-spin" /> Loading pricing…
            </div>
          ) : (
            <>
              <div
                style={{
                  marginBottom: "1rem",
                  padding: "0.5rem 0.75rem",
                  borderRadius: 8,
                  fontSize: "0.85rem",
                  fontWeight: 600,
                  background: isConfigured ? "rgba(22, 101, 52, 0.08)" : "rgba(234, 179, 8, 0.12)",
                  color: isConfigured ? "#166534" : "#a16207",
                }}
              >
                {isConfigured ? "Pricing is configured" : "Pricing not configured yet — save to enable convert"}
              </div>

              <div className="bonus-form-grid">
                <div className="bonus-form-group">
                  <label>USDT price</label>
                  <input
                    type="number"
                    step="any"
                    min="0.00000001"
                    value={form.usdt_price}
                    onChange={(e) => updateField("usdt_price", e.target.value)}
                    placeholder="e.g. 1"
                  />
                  <small style={{ color: "#64748b", fontSize: "0.78rem" }}>
                    Reference USDT unit (typically 1)
                  </small>
                </div>
                <div className="bonus-form-group">
                  <label>ZILN price (USDT)</label>
                  <input
                    type="number"
                    step="any"
                    min="0.00000001"
                    value={form.zil_price}
                    onChange={(e) => updateField("zil_price", e.target.value)}
                    placeholder="e.g. 0.05"
                  />
                  <small style={{ color: "#64748b", fontSize: "0.78rem" }}>
                    Price of 1 ZILN in USDT
                  </small>
                </div>
              </div>

              {rateHint ? (
                <p style={{ margin: "0.75rem 0 0", fontSize: "0.88rem", color: "#0f766e", fontWeight: 600 }}>
                  <i className="fas fa-calculator" style={{ marginRight: 6 }} />
                  {rateHint}
                </p>
              ) : null}
            </>
          )}

          {message.text ? (
            <p
              className={`bonus-message bonus-message--${message.type === "error" ? "error" : "success"}`}
              role="status"
            >
              {message.text}
            </p>
          ) : null}

          <div className="bonus-actions">
            <button
              type="button"
              className="cp-btn cp-btn--outline"
              onClick={fetchPricing}
              disabled={loading || saving}
            >
              <i className="fas fa-rotate-right" /> Refresh
            </button>
            <button
              type="button"
              className="cp-btn cp-btn--primary"
              onClick={handleSave}
              disabled={loading || saving}
            >
              {saving ? (
                <>
                  <i className="fas fa-spinner fa-spin" /> Saving…
                </>
              ) : (
                <>
                  <i className="fas fa-save" /> Save prices
                </>
              )}
            </button>
          </div>
        </section>
      </div>
    </main>
  );
};

export default ZilPricingSettingsPage;
